import { useCallback, useEffect, useRef, useState } from 'react';
import { speak, stopSpeaking, ttsSupported } from '@/lib/tts/speak';

/**
 * Talking with the chat (docs/ai-chat.md, "Talking"): the answers are read aloud with the
 * speech synthesis of the browser (`lib/tts/speak.ts`, the voice and rate of the profile),
 * sentence by sentence while they stream. `useSpeaker` holds the mute switch (remembered in
 * the browser) and whether something is being spoken right now, so the microphone can wait.
 */
const MUTE_KEY = 'recipes:aiChatMute';

/** The text of a Markdown answer the way it should be spoken: no markers, links by their text. */
export function speakable(markdown: string): string {
  return markdown
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+/gm, '')
    .replace(/^\s*\[( |x)\]\s*/gim, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(\S.*?)\1/g, '$2')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*[-*_]{3,}\s*$/gm, '')
    .replace(/^\s*\|?[\s:|-]+\|[\s:|-]*$/gm, '')
    .replace(/\s*\|\s*/g, ', ')
    .replace(/https?:\/\/\S+/g, 'Link')
    .replace(/\s+/g, ' ')
    .replace(/^[,\s]+|[,\s]+$/g, '')
    .trim();
}

/**
 * Collects the streamed deltas of an answer and hands out complete sentences (or lines), so
 * the voice starts before the answer is finished and long answers are not cut off by the
 * browser. Code blocks are announced, not read.
 */
export class SentenceBuffer {
  private text = '';
  private code = false;

  push(chunk: string): string[] {
    this.text += chunk;
    const out: string[] = [];
    const boundary = /^[\s\S]*?(?:[.!?…]+["“»)]*(?=\s)|\n)/;
    let m: RegExpExecArray | null;
    while ((m = boundary.exec(this.text))) {
      this.take(m[0], out);
      this.text = this.text.slice(m[0].length);
    }
    if (this.text.length > 400 && !this.code) {
      const i = this.text.lastIndexOf(' ', 400);
      if (i > 0) {
        this.take(this.text.slice(0, i), out);
        this.text = this.text.slice(i + 1);
      }
    }
    return out;
  }

  flush(): string[] {
    const out: string[] = [];
    if (this.text.trim() && !this.code) this.take(this.text, out);
    this.text = '';
    return out;
  }

  private take(segment: string, out: string[]) {
    if (/^\s*```/.test(segment)) {
      this.code = !this.code;
      return;
    }
    if (this.code) return;
    const spoken = speakable(segment);
    if (spoken) out.push(spoken);
  }
}

export interface SpeakerOptions {
  lang: string;
  rate?: number;
  voiceName?: string | null;
}

export function useSpeaker(options: SpeakerOptions) {
  const [muted, setMutedState] = useState(() => {
    try {
      return localStorage.getItem(MUTE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [speaking, setSpeaking] = useState(false);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  // The sentences are spoken one after the other; `speak()` cancels whatever is still playing.
  const chain = useRef<Promise<void>>(Promise.resolve());
  const generation = useRef(0);
  const pending = useRef(0);
  const idle = useRef<Array<() => void>>([]);

  const settle = useCallback(() => {
    pending.current = 0;
    setSpeaking(false);
    const waiting = idle.current;
    idle.current = [];
    for (const fn of waiting) fn();
  }, []);
  const cancel = useCallback(() => {
    generation.current++;
    chain.current = Promise.resolve();
    stopSpeaking();
    settle();
  }, [settle]);
  const say = useCallback(
    (text: string) => {
      if (mutedRef.current || !ttsSupported() || !text.trim()) return;
      const gen = generation.current;
      pending.current++;
      setSpeaking(true);
      chain.current = chain.current.then(async () => {
        if (gen !== generation.current) return;
        await speak(text, optionsRef.current).catch(() => undefined);
        if (gen !== generation.current) return;
        pending.current--;
        if (pending.current <= 0) settle();
      });
    },
    [settle],
  );
  /** Runs `fn` once nothing is being spoken any more (at once when the voice is quiet). */
  const whenQuiet = useCallback((fn: () => void) => {
    if (pending.current <= 0) fn();
    else idle.current.push(fn);
  }, []);
  const setMuted = useCallback(
    (m: boolean) => {
      setMutedState(m);
      try {
        localStorage.setItem(MUTE_KEY, m ? '1' : '0');
      } catch {
        /* private mode */
      }
      if (m) cancel();
    },
    [cancel],
  );
  useEffect(() => () => stopSpeaking(), []);
  return { supported: ttsSupported(), muted, setMuted, speaking, say, cancel, whenQuiet };
}
